import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { orderBy } from 'lodash';
import { ProgramConsumer } from '../../contexts/ProgramContext';
import { trackNames, FIRST_DAY, LAST_DAY, talkTypes, YEAR } from '../../constants';
import { isLiveCheck, isConferenceDayCheck, handleShowLive } from '../../utils/helpers';
import ProgramCard from './components/ProgramCard';
import {
  breaks,
  placeholders,
  conferenceOpening,
  workshopPlaceholders
  // keyNote
} from './constants';

import './Program.scss';

export const Program = ({
  loadProgram,
  talks,
  selectedTalk,
  speakers,
  setSelectedTalk,
  isLoading
}) => {
  const history = useHistory();
  const location = useLocation();

  const [trigger, setTrigger] = useState(new Date());
  const [isConferenceDay, setIsConferenceDay] = useState(false);

  useEffect(() => {
    loadProgram();
    if (history.action !== 'POP') {
      window.scrollTo(0, 0);
      setSelectedTalk('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) setTrigger(new Date());
    };
    if (location.pathname === '/program') {
      document.addEventListener('visibilitychange', handleVisibility);
      setIsConferenceDay(isConferenceDayCheck());
      return () => {
        document.removeEventListener('visibilitychange', handleVisibility);
      };
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === '/program') {
      const interval = setInterval(() => {
        setTrigger(new Date());
      }, 1000 * 60);
      return () => clearInterval(interval);
    }
  }, [trigger, location.pathname]);

  const renderProgramRow = (talksInRow, time) => {
    const placeholdersExist = placeholders.find(placeholder => placeholder.startTime === time);
    const workshopPlaceholderExist = workshopPlaceholders.find(
      placeholder => placeholder.startTime === time
    );
    let slots = talksInRow.length > 0 ? [...talksInRow] : [];
    if (placeholdersExist && location.pathname === '/program') {
      slots = [...talksInRow, ...placeholdersExist.groupedTalks];
    } else if (workshopPlaceholderExist && location.pathname !== '/program') {
      slots = [...talksInRow, ...workshopPlaceholderExist.groupedTalks];
    }

    let mainTalks;
    let lightningTalks;
    let orderedLightningTalks;
    if (location.pathname === '/program') {
      mainTalks = slots.filter(talk => talk.trackInDay !== 6);
      lightningTalks = slots.filter(talk => talk.trackInDay === 6);
      orderedLightningTalks =
        lightningTalks.length > 0 ? orderBy(lightningTalks, ['order'], 'asc') : [];
    } else {
      mainTalks = slots;
    }

    const orderedTalks = mainTalks.length > 0 ? orderBy(mainTalks, ['trackInDay'], 'asc') : [];

    if (lightningTalks && lightningTalks.length) {
      orderedTalks.push(orderedLightningTalks);
    }

    const columnWidth = location.pathname === '/program' ? 'is-2' : '';
    const columnClasses = orderedTalks.length === 1 ? 'is-12' : `is-12-touch ${columnWidth}`;

    if (orderedTalks.length && orderedTalks[0].isBreak) {
      return <div className="break-slot column is-12">{orderedTalks[0].title}</div>;
    }

    return orderedTalks.map(slot => {
      let speakerNames = [];
      if (!Array.isArray(slot)) {
        const color = location.pathname === '/program' ? slot.trackInDay : 'workshop';
        if (!slot.isBreak && !slot.isConferenceOpening && slot.speakers) {
          slot.speakers.forEach(speakerId => {
            const foundSpeaker = speakers.find(sp => sp.id === speakerId) || {};
            if (foundSpeaker && foundSpeaker.name) {
              speakerNames.push(foundSpeaker);
            }
          });
        } else if (slot.isConferenceOpening) {
          speakerNames = [...slot.speakers];
        }

        return (
          <div key={slot.id || slot.title} className={`column ${columnClasses}`}>
            <ProgramCard
              key={slot.id || slot.title}
              color={color}
              id={slot.id}
              isConferenceDay={isConferenceDay}
              isLive={location.pathname === '/program' ? isLiveCheck(slot, trigger) : false}
              onCardClick={setSelectedTalk}
              selectedTalk={selectedTalk}
              speakers={speakerNames}
              talk={slot}
              tileRole={slot.tileRole || ''}
            />
          </div>
        );
      }
      return (
        <div key="slot6" className={`column ${columnClasses} lightningTalks`}>
          {slot.map(lightningTalk => {
            const lightningSpeakerNames = [];
            if (lightningTalk.speakers) {
              lightningTalk.speakers.forEach(speakerId => {
                const foundSpeaker = speakers.find(sp => sp.id === speakerId) || {};
                if (foundSpeaker && foundSpeaker.name) {
                  lightningSpeakerNames.push(foundSpeaker);
                }
              });
            }

            const color = 6;
            return (
              <ProgramCard
                key={lightningTalk.id || lightningTalk.title}
                color={color}
                id={lightningTalk.id}
                isConferenceDay={isConferenceDay}
                isLive={isLiveCheck(lightningTalk, trigger)}
                onCardClick={setSelectedTalk}
                selectedTalk={selectedTalk}
                speakers={lightningSpeakerNames}
                talk={lightningTalk}
                tileRole={lightningTalk.tileRole || ''}
              />
            );
          })}
        </div>
      );
    });
  };

  const renderProgram = () => {
    let filteredTalks = [];
    // let designThinkingWorkshop = null;
    if (location.pathname === '/program') {
      filteredTalks = talks.filter(
        talk => talk.track === talkTypes.talk || talk.track === talkTypes.lightningTalk
      );
      filteredTalks.push(conferenceOpening);
      // filteredTalks.push(keyNote);
    } else {
      filteredTalks = talks.filter(talk => talk.track === 'Interactive 3-hour Hands-on Lab');
      // designThinkingWorkshop = filteredTalks.find(
      //   talk => talk.id === '81deeef0-b68a-4ed5-bfc3-d95a31be0ae1'
      // );
    }

    const uniqueStartTimes = [...new Set(filteredTalks.map(item => item.startTime))];
    const talksByTime = [];

    // eslint-disable-next-line no-restricted-syntax
    for (const startTime of uniqueStartTimes) {
      const groupedTalksByTime = filteredTalks.filter(talk => talk.startTime === startTime);
      // if (
      //   !groupedTalksByTime.find(talk => talk.id === '81deeef0-b68a-4ed5-bfc3-d95a31be0ae1') &&
      //   location.pathname === '/workshops'
      // ) {
      //   const enchancedDesignThinking = { ...designThinkingWorkshop };
      //   enchancedDesignThinking.tileRole = 'CLOSED';
      //   groupedTalksByTime.push(enchancedDesignThinking);
      // }
      talksByTime.push({ startTime, groupedTalks: groupedTalksByTime });
    }

    const mergedTalksAndBreaks =
      location.pathname === '/program' ? [...breaks, ...talksByTime] : [...talksByTime];
    const orderedTalksAndBreaks = orderBy(mergedTalksAndBreaks, ['startTime'], 'asc');

    return (
      <div className="program-wrapper is-full">
        {location.pathname !== '/program' ? (
          <h2 className="subtitle">
            <div className="note">
              <span className="bold-text">Hands-on Lab Program for OpenSlava 2025</span>
              The labs are scheduled to take place on Wednesday, October 15, 2025, at FEI STU,
              Ilkovicova 3, Bratislava, Slovakia. They will be conducted in English and are free of
              charge for registered participants.
            </div>
          </h2>
        ) : null}
        {filteredTalks.length > 0 ? (
          orderedTalksAndBreaks.map(row => (
            <div key={row.startTime} className="program-row">
              <div className="program-row-time">{row.startTime}</div>
              <div className="columns program-row-talks">
                {renderProgramRow(row.groupedTalks, row.startTime)}
              </div>
            </div>
          ))
        ) : (
          // <h2 className="subtitle">There is no program at this time, stay tuned.</h2>
          <h2 className="subtitle">
            <div className="note">
              <span className="bold-text">
                The Hands-on Lab program for OpenSlava {YEAR} will be published soon, please stay
                tuned.
              </span>
              Registration for the Hands-on Labs will open approximately two weeks prior to the
              event for all OpenSlava {YEAR} registrants. The labs are scheduled for Wednesday,
              October {FIRST_DAY}, {YEAR}, and will be held at FEI STU, Ilkovicova 3, Bratislava,
              Slovakia. The labs will be conducted in English and are free for registered
              participants.
            </div>
          </h2>
        )}
      </div>
    );
  };

  return (
    <section className="section" id="program-section">
      <div className="container program-schedule">
        {isConferenceDayCheck() ? (
          <button className="button live-button" onClick={handleShowLive} type="button">
            <span className="fa-stack">
              <i className="fas fa-circle fa-stack-1x" />
            </span>
            <span className="button-text">Show LIVE</span>
          </button>
        ) : null}
        <h1 className="">{location.pathname === '/program' ? 'PROGRAM' : 'HANDS-ON LABS'}</h1>
        <h2 className="subtitle">
          {location.pathname === '/program' ? (
            <div>
              <span className="note">
                Please note that the program is subject to potential changes. Continuous updates are
                possible leading up to the conference. We recommend checking this website
                periodically for the most current information.
              </span>
            </div>
          ) : null
          // (
          //   <div>
          //     {/* <span className="note">
          //       Please note that the program is subject to potential changes. Continuous updates are
          //       possible leading up to the conference. We recommend checking this website
          //       periodically for the most current information.
          //     </span> */}
          //     {/* <div className="note bold-text">Hands-On Labs registration will open soon.</div> */}
          //   </div>
          // )
          }
          <div>
            <span className="date">
              {location.pathname === '/workshops' ? `Oct, ${FIRST_DAY}` : `Oct, ${LAST_DAY}`}
            </span>
            <span className="conference-day">
              {location.pathname === '/workshops' ? 'Day 1' : 'Day 2'}
            </span>
          </div>
        </h2>
        {location.pathname === '/program' ? (
          <div className="track-names-row">
            <div className="track-name">
              <div className="track-marker track1-color" />
              <div className="track-title">{trackNames.tr1}</div>
            </div>
            <div className="track-name">
              <div className="track-marker track2-color" />
              <div className="track-title">{trackNames.tr2}</div>
            </div>
            <div className="track-name">
              <div className="track-marker track3-color" />
              <div className="track-title">{trackNames.tr3}</div>
            </div>
            <div className="track-name">
              <div className="track-marker track4-color" />
              <div className="track-title">{trackNames.tr4}</div>
            </div>
            <div className="track-name">
              <div className="track-marker track5-color" />
              <div className="track-title">{trackNames.tr5}</div>
            </div>
            <div className="track-name">
              <div className="track-marker track6-color" />
              <div className="track-title">{trackNames.tr6}</div>
            </div>
          </div>
        ) : null}
        {!isLoading ? (
          renderProgram()
        ) : (
          <i className="fa fa-spinner fa-spin fa-3x fa-fw" style={{ color: '#7500c0' }} />
        )}
      </div>
    </section>
  );
};

Program.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  loadProgram: PropTypes.func.isRequired,
  selectedTalk: PropTypes.string.isRequired,
  setSelectedTalk: PropTypes.func.isRequired,
  speakers: PropTypes.arrayOf(PropTypes.any).isRequired,
  talks: PropTypes.arrayOf(PropTypes.any).isRequired
};

const ProgramContainer = (...props) => (
  <ProgramConsumer>
    {({ loadProgram, talks, selectedTalk, speakers, setSelectedTalk, isLoading }) => (
      <Program
        isLoading={isLoading}
        loadProgram={loadProgram}
        selectedTalk={selectedTalk}
        setSelectedTalk={setSelectedTalk}
        speakers={speakers}
        talks={talks}
        {...props}
      />
    )}
  </ProgramConsumer>
);

export default ProgramContainer;
